// src/components/IdleWarningModal.tsx
// Модалка предупреждения об автовыходе
import React from 'react';
import { Clock, LogOut, Loader2 } from 'lucide-react';

interface Props {
  secondsLeft: number;
  onExtend: () => void;
  onLogout: () => void;
}

const IdleWarningModal: React.FC<Props> = ({ secondsLeft, onExtend, onLogout }) => {
  const isExpired = secondsLeft <= 0;
  const mins = Math.floor(secondsLeft / 60);
  const secs = secondsLeft % 60;
  const timeStr = mins > 0 ? `${mins}:${String(secs).padStart(2, '0')}` : `${secs} сек`;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 99999,
      background: 'rgba(15,23,42,0.65)', backdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20,
    }}>
      <div style={{
        background: '#fff', borderRadius: 20, maxWidth: 440, width: '100%',
        boxShadow: '0 25px 60px rgba(15,23,42,0.4)',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding: '28px 28px 20px',
          background: isExpired
            ? 'linear-gradient(135deg, #ef4444, #dc2626)'
            : 'linear-gradient(135deg, #f59e0b, #d97706)',
          color: '#fff', textAlign: 'center',
          transition: 'background 0.3s',
        }}>
          <div style={{
            width: 64, height: 64, margin: '0 auto 12px',
            background: 'rgba(255,255,255,0.2)', borderRadius: 18,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {isExpired ? (
              <Loader2 size={32} color="#fff" style={{ animation: 'spin 1s linear infinite' }} />
            ) : (
              <Clock size={32} color="#fff" />
            )}
          </div>
          <div style={{ fontSize: 20, fontWeight: 900 }}>
            {isExpired ? 'Выходим...' : 'Сессия истекает'}
          </div>
          <div style={{ fontSize: 13, opacity: 0.9, marginTop: 4 }}>
            {isExpired ? 'Выполняется автоматический выход' : 'Вы неактивны уже долгое время'}
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '24px 28px', textAlign: 'center' }}>
          <div style={{
            fontSize: 42, fontWeight: 900,
            color: isExpired ? '#94a3b8' : '#ef4444',
            marginBottom: 8, fontVariantNumeric: 'tabular-nums',
            transition: 'color 0.3s',
          }}>
            {isExpired ? '—' : timeStr}
          </div>
          <div style={{ fontSize: 14, color: '#475569', lineHeight: 1.6, marginBottom: 20 }}>
            {isExpired ? (
              <>Выход из системы выполняется автоматически. Пожалуйста, подождите.</>
            ) : (
              <>
                Через <b>{timeStr}</b> вы автоматически выйдете из системы.
                Все несохранённые изменения могут быть потеряны.
              </>
            )}
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={onExtend}
              disabled={isExpired}
              style={{
                flex: 1, padding: '12px 20px',
                background: isExpired
                  ? '#e2e8f0'
                  : 'linear-gradient(135deg, #10b981, #059669)',
                color: isExpired ? '#94a3b8' : '#fff',
                border: 'none', borderRadius: 12,
                cursor: isExpired ? 'not-allowed' : 'pointer',
                fontSize: 14, fontWeight: 700,
                fontFamily: 'inherit',
                boxShadow: isExpired ? 'none' : '0 8px 20px -4px rgba(16,185,129,0.5)',
                transition: 'all 0.2s',
              }}
            >
              Остаться в системе
            </button>
            <button
              onClick={onLogout}
              disabled={isExpired}
              style={{
                padding: '12px 20px',
                background: '#fff', border: '1px solid #e2e8f0',
                color: '#64748b', borderRadius: 12,
                cursor: isExpired ? 'not-allowed' : 'pointer',
                fontSize: 14, fontWeight: 600,
                fontFamily: 'inherit',
                display: 'inline-flex', alignItems: 'center', gap: 6,
                opacity: isExpired ? 0.5 : 1,
                transition: 'all 0.2s',
              }}
            >
              <LogOut size={14} /> Выйти
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default IdleWarningModal;
